import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type PermAction = "view" | "edit";

export type PermissionRow = {
  permission_key: string;
  can_view: boolean;
  can_edit: boolean;
};

/**
 * Loads the current user's effective permissions (admin = full).
 * Use `can(key, 'view'|'edit')` to gate UI.
 */
export function usePermissions() {
  const { user, isAdmin, loading: authLoading } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["my_permissions", user?.id],
    enabled: !!user && !authLoading,
    queryFn: async (): Promise<PermissionRow[]> => {
      if (!user) return [];
      const { data, error } = await supabase.rpc("user_permissions", { _user_id: user.id });
      if (error) {
        console.error("user_permissions failed", error);
        return [];
      }
      return (data ?? []) as PermissionRow[];
    },
  });

  const map = new Map<string, { view: boolean; edit: boolean }>();
  (data ?? []).forEach((r) =>
    map.set(r.permission_key, { view: !!r.can_view, edit: !!r.can_edit })
  );

  const can = (key: string, action: PermAction = "view") => {
    if (isAdmin) return true;
    const p = map.get(key);
    if (!p) return false;
    return action === "edit" ? p.edit : p.view;
  };

  return {
    isAdmin,
    can,
    permissions: data ?? [],
    loading: authLoading || isLoading,
  };
}
